/* ==========================================================================
   Theme registry — the single source of truth for available themes.

   To add a theme:
     1. Create  src/themes/<id>.css  with a `[data-theme="<id>"] { … }` block
        (copy `_template.css`).
     2. `import './<id>.css'` below.
     3. Add an entry to THEMES.
   The switcher picks up new themes automatically from THEMES.
   ========================================================================== */

// Side-effect imports — bundles each theme's CSS. The `[data-theme]` selector
// means only the active one applies at runtime.
import './novaquantis.css';
import './experimental.css';

export interface ThemeDef {
  /** Must match the CSS `[data-theme="<id>"]` selector and localStorage value. */
  id: string;
  /** Human label shown in the switcher. */
  label: string;
  /** One-line description (optional, shown as a tooltip / subtitle). */
  blurb?: string;
}

export const THEMES: ThemeDef[] = [
  {
    id: 'novaquantis',
    label: 'NovaQuantis',
    blurb: 'The current production look — navy + electric blue + gold.',
  },
  {
    id: 'experimental',
    label: 'Experimental',
    blurb: 'A starter alternate — plum canvas, violet + teal, mint/coral.',
  },
];

export const DEFAULT_THEME = THEMES[0].id;
export const STORAGE_KEY = 'nq-design-lab-theme';

/** Resolve the initial theme: persisted choice if valid, else the default. */
export function getInitialTheme(): string {
  if (typeof localStorage !== 'undefined') {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && THEMES.some((t) => t.id === saved)) return saved;
  }
  return DEFAULT_THEME;
}

/** Apply a theme to <html> and persist it. */
export function applyTheme(id: string): void {
  document.documentElement.dataset.theme = id;
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* localStorage unavailable (private mode) — non-fatal. */
  }
}
